import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Plus, Pencil, Trash2, X, Users2, Loader2, ChevronLeft, ChevronRight, Calendar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import DotazioneSection from "../components/DotazioneSection";

const COLORS = ["#1B5E20", "#0D47A1", "#B71C1C", "#E65100", "#4A148C", "#00695C", "#33691E", "#827717"];

function startOfWeekSunday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // getDay(): Sun=0…Sat=6. Vogliamo la domenica più recente.
  d.setDate(d.getDate() - d.getDay());
  return d;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Squadre() {
  const navigate = useNavigate();
  const [squadre, setSquadre] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: COLORS[0], members: [], email: "", note: "" });
  const [weekStart, setWeekStart] = useState(isoDate(startOfWeekSunday(new Date())));
  const [calendar, setCalendar] = useState(null);
  const [planModal, setPlanModal] = useState(null);       // {squadra, date}
  const [pickIntervento, setPickIntervento] = useState(null); // {interventi, squadraName, date}

  const load = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([api.get("/squadre"), api.get("/users")]);
      setSquadre(s.data);
      setUsers(u.data);
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = useCallback(async () => {
    try {
      const { data } = await api.get(`/squadre/calendario-settimana?week_start=${weekStart}`);
      setCalendar(data);
    } catch { /* */ }
  }, [weekStart]);

  useEffect(() => { load(); }, []);
  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(isoDate(d));
  };
  const goToday = () => setWeekStart(isoDate(startOfWeekSunday(new Date())));

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", color: COLORS[0], members: [], email: "", note: "" });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, color: s.color || COLORS[0], members: s.members || [], email: s.email || "", note: s.note || "" });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/squadre/${editing.id}`, form);
        toast.success("Squadra aggiornata");
      } else {
        await api.post("/squadre", form);
        toast.success("Squadra creata");
      }
      setShowForm(false);
      await load();
      await loadCalendar();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Eliminare la squadra "${s.name}"? Gli appuntamenti collegati perderanno l'assegnazione.`)) return;
    try {
      await api.delete(`/squadre/${s.id}`);
      toast.success("Squadra eliminata");
      await load();
      await loadCalendar();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleMember = (uid) => {
    setForm((f) => f.members.includes(uid)
      ? { ...f, members: f.members.filter((x) => x !== uid) }
      : { ...f, members: [...f.members, uid] });
  };

  return (
    <div data-testid="squadre-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Squadre</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {squadre.length} squadre attive · gestione team installazione
          </p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-squadra-btn">
          <Plus className="w-4 h-4" /> Nuova squadra
        </button>
      </div>

      {/* Week navigator */}
      <div className="card-shell p-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="week-navigator">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <span className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>
            Settimana {calendar?.week_start ? new Date(calendar.week_start).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—"}
            {" → "}
            {calendar?.week_end ? new Date(calendar.week_end).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftWeek(-1)} className="p-1.5 rounded-md border hover:bg-black/5" style={{ borderColor: "var(--border)" }} data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goToday} className="px-3 py-1.5 rounded-md border text-xs font-semibold hover:bg-black/5" style={{ borderColor: "var(--border)" }} data-testid="week-today">
            Oggi
          </button>
          <button onClick={() => shiftWeek(1)} className="p-1.5 rounded-md border hover:bg-black/5" style={{ borderColor: "var(--border)" }} data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Workload overview */}
      <WorkloadOverview squadre={squadre} calendar={calendar} />

      {loading ? (
        <div className="p-8 flex items-center justify-center text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…</div>
      ) : squadre.length === 0 ? (
        <div className="card-shell p-12 text-center">
          <Users2 className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Nessuna squadra creata</div>
          <button onClick={openNew} className="btn-primary mt-4">Crea la prima squadra</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {squadre.map((s) => (
            <div key={s.id} className="card-shell p-5" data-testid={`squadra-${s.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: s.color }}>
                  {s.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{s.name}</div>
                  {s.email && <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{s.email}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded hover:bg-black/5" data-testid={`edit-squadra-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s)} className="p-1.5 rounded hover:bg-black/5" data-testid={`delete-squadra-${s.id}`}><Trash2 className="w-4 h-4" style={{ color: "#B30000" }} /></button>
                </div>
              </div>
              <div className="text-xs mb-2 font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                Membri ({s.members_detail?.length || 0})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(s.members_detail || []).map((m) => (
                  <span key={m.id} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-muted)" }}>
                    {m.name}
                  </span>
                ))}
                {(!s.members_detail || s.members_detail.length === 0) && (
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Nessun membro assegnato</span>
                )}
              </div>
              {s.note && <div className="mt-3 text-xs pt-3 border-t" style={{ borderColor: "var(--border)", color: "var(--text-muted)" }}>{s.note}</div>}

              {/* Mini calendario settimanale (Dom → Dom) */}
              <WeekStrip
                cells={(calendar?.squadre || []).find((sq) => sq.squadra_id === s.id)?.cells}
                color={s.color}
                total={(calendar?.squadre || []).find((sq) => sq.squadra_id === s.id)?.totale_settimana || 0}
                squadraId={s.id}
                squadraName={s.name}
                onCellClick={(cell) => {
                  // Cella vuota → apri modal nuova pianificazione per questa squadra in quella data
                  if (cell.count === 0) {
                    setPlanModal({ squadra: s, date: cell.date, isWeekend: cell.is_weekend, holiday: cell.holiday_name });
                    return;
                  }
                  // Cella con 1 intervento → vai al cliente
                  if (cell.count === 1 && cell.interventi?.[0]?.cliente_id) {
                    navigate(`/clienti/${cell.interventi[0].cliente_id}`);
                    return;
                  }
                  // Multiple → mostra selettore
                  setPickIntervento({ interventi: cell.interventi || [], squadraName: s.name, date: cell.date });
                }}
                onMove={async ({ cliente_id, target_date, target_squadra_id, target_holiday }) => {
                  if (target_holiday) {
                    const ok = window.confirm(
                      `Stai spostando un appuntamento al ${target_date} che è ${target_holiday}. Procedere comunque?`
                    );
                    if (!ok) return;
                  }
                  try {
                    await api.patch(`/clienti/${cliente_id}/pianificazione/move`, {
                      new_date: target_date,
                      new_squadra_id: target_squadra_id,
                    });
                    toast.success("Appuntamento spostato", {
                      description: "Vuoi notificare la nuova data al cliente?",
                      duration: 8000,
                      action: {
                        label: "Notifica WhatsApp 📲",
                        onClick: () => navigate(`/clienti/${cliente_id}?wa=variazione`),
                      },
                    });
                    loadCalendar();
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
              />

              {/* Attrezzature & Mezzi */}
              <DotazioneSection squadra={s} />
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={() => setShowForm(false)}>          <div className="card-shell w-full max-w-xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="squadra-form-modal">
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <h2 className="text-xl font-bold" style={{ fontFamily: "Manrope" }}>{editing ? "Modifica squadra" : "Nuova squadra"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Nome squadra *</label>
                <input data-testid="form-squadra-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Colore</label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                      className="w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110"
                      style={{ background: c, borderColor: form.color === c ? "#000" : "transparent" }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Email contatto</label>
                <input data-testid="form-squadra-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Membri (utenti)</label>
                <div className="mt-2 max-h-48 overflow-y-auto border rounded-md p-2" style={{ borderColor: "var(--border)" }}>
                  {users.length === 0 ? (
                    <div className="text-xs p-2" style={{ color: "var(--text-muted)" }}>Nessun utente disponibile</div>
                  ) : users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-black/5 cursor-pointer text-sm">
                      <input type="checkbox" checked={form.members.includes(u.id)} onChange={() => toggleMember(u.id)}
                        data-testid={`form-member-${u.id}`} />
                      <span>{u.name}</span>
                      <span className="text-xs" style={{ color: "var(--text-muted)" }}>({u.role})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
                <button type="submit" className="btn-primary" data-testid="form-squadra-submit">{editing ? "Aggiorna" : "Crea squadra"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {planModal && (
        <CreatePianificazioneModal
          squadra={planModal.squadra}
          date={planModal.date}
          isWeekend={planModal.isWeekend}
          holidayName={planModal.holiday}
          onClose={() => setPlanModal(null)}
          onSaved={() => { setPlanModal(null); loadCalendar(); }}
        />
      )}

      {pickIntervento && (
        <PickInterventoModal
          interventi={pickIntervento.interventi}
          squadraName={pickIntervento.squadraName}
          date={pickIntervento.date}
          onClose={() => setPickIntervento(null)}
          onPick={(cid) => { setPickIntervento(null); navigate(`/clienti/${cid}`); }}
        />
      )}
    </div>
  );
}


function WeekStrip({ cells, color, total, squadraId, squadraName, onCellClick, onMove }) {
  const [dragOverIdx, setDragOverIdx] = React.useState(null);
  if (!cells) return null;

  const handleDragStart = (e, cell) => {
    // Solo le celle con esattamente 1 intervento sono draggabili
    if (cell.count !== 1) { e.preventDefault(); return; }
    const iv = cell.interventi?.[0];
    if (!iv?.cliente_id) { e.preventDefault(); return; }
    const payload = {
      cliente_id: iv.cliente_id,
      cliente_nome: iv.cliente_nome,
      source_squadra_id: squadraId,
      source_date: cell.date,
    };
    e.dataTransfer.setData("application/x-pianificazione", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, cell) => {
    // Non droppare su se stessi né su celle già occupate
    e.preventDefault();
    if (cell.count > 0) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(cell.index);
  };

  const handleDragLeave = () => setDragOverIdx(null);

  const handleDrop = (e, cell) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (cell.count > 0) return;
    const raw = e.dataTransfer.getData("application/x-pianificazione");
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch { return; }
    if (!payload.cliente_id) return;
    if (payload.source_squadra_id === squadraId && payload.source_date === cell.date) return;
    onMove && onMove({
      cliente_id: payload.cliente_id,
      target_date: cell.date,
      target_squadra_id: squadraId,
      target_holiday: cell.holiday_name || (cell.is_weekend ? "Weekend" : null),
    });
  };

  return (
    <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid={`week-strip-${squadraId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
          Settimana Dom → Dom
        </div>
        <div className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{
          background: total > 0 ? `${color}22` : "var(--surface-muted)",
          color: total > 0 ? color : "var(--text-muted)",
        }}>
          {total} {total === 1 ? "intervento" : "interventi"}
        </div>
      </div>
      <div className="grid grid-cols-8 gap-1">
        {cells.map((c) => {
          const empty = c.count === 0;
          const intensity = Math.min(c.count, 4);  // 0..4
          const isRedDay = c.is_weekend || c.is_holiday;
          const bg = empty
            ? (isRedDay ? "#FEE2E2" : "#F3F4F6")
            : (isRedDay ? "#DC2626" : color);
          const opacity = empty ? 1 : 0.3 + intensity * 0.175;  // 0.3 → 1.0
          const fg = empty ? (isRedDay ? "#991B1B" : "#9CA3AF") : "#fff";
          const borderColor = c.is_today
            ? "#111827"
            : (isRedDay && empty ? "#FCA5A5" : "transparent");
          const titleExtra = c.is_holiday ? `\n🎉 ${c.holiday_name}` : (c.is_weekend ? "\n(Weekend)" : "");
          const isDragOver = dragOverIdx === c.index;
          const draggable = c.count === 1;
          return (
            <button
              key={c.index}
              type="button"
              draggable={draggable}
              onDragStart={(e) => handleDragStart(e, c)}
              onDragOver={(e) => handleDragOver(e, c)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, c)}
              onClick={() => onCellClick && onCellClick(c)}
              className={`relative aspect-square rounded-md flex flex-col items-center justify-center transition focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--primary)] ${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${isDragOver ? "ring-2 ring-blue-500 scale-105" : "hover:scale-105 hover:shadow"}`}
              style={{
                background: isDragOver ? "#DBEAFE" : bg,
                opacity: isDragOver ? 1 : opacity,
                border: c.is_today ? "2px solid #111827" : (isDragOver ? "2px dashed #2563EB" : `1px solid ${borderColor}`),
              }}
              title={`${c.weekday_label} ${c.date.slice(8, 10)}/${c.date.slice(5, 7)}${titleExtra}\n${c.tooltip}\n${empty ? "▸ Clicca per creare un appuntamento · trascina qui per spostare" : draggable ? "▸ Clicca per aprire · trascina per spostare" : "▸ Clicca per aprire"}`}
              data-testid={`week-cell-${squadraId}-${c.index}`}
            >
              <div className="text-[9px] uppercase font-bold leading-none" style={{ color: fg }}>{c.weekday_label}</div>
              <div className="text-xs font-extrabold leading-none mt-0.5" style={{ color: fg }}>{c.date.slice(8, 10)}</div>
              {c.is_holiday && (
                <div
                  className="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full"
                  style={{ background: "#DC2626", boxShadow: "0 0 0 1.5px white" }}
                  title={c.holiday_name}
                  data-testid={`week-cell-holiday-${squadraId}-${c.index}`}
                />
              )}
              {!empty && (
                <div className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center" style={{ background: "#111827", color: "white" }}>
                  {c.count}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-2 text-[10px]" style={{ color: "var(--text-muted)" }}>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#DC2626" }} />
          Festa / Weekend
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm inline-block border-2" style={{ borderColor: "#111827" }} />
          Oggi
        </span>
        <span className="inline-flex items-center gap-1 ml-auto italic" style={{ color: "var(--text-muted)" }}>
          Clicca per creare · trascina per spostare
        </span>
      </div>
    </div>
  );
}


// ---------- Modal: nuova pianificazione da cella libera ----------
function CreatePianificazioneModal({ squadra, date, isWeekend, holidayName, onClose, onSaved }) {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    cliente_id: "",
    ora: "09:00",
    durata_ore: 4.0,
    pannelli: "",
    batteria: "",
    inverter: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const isRedDay = isWeekend || !!holidayName;
  const [ackRedDay, setAckRedDay] = useState(!isRedDay);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/clienti", { params: { sort_by: "nome_asc" } });
        if (alive) setClienti(Array.isArray(data) ? data : (data?.items || []));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = clienti.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (c.nome || "").toLowerCase().includes(s) ||
      (c.cognome_ragione_sociale || "").toLowerCase().includes(s) ||
      (c.cliente_id || "").toLowerCase().includes(s) ||
      (c.citta || "").toLowerCase().includes(s)
    );
  }).slice(0, 200);

  const labelDate = new Date(date + "T00:00:00").toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) { toast.error("Seleziona un cliente"); return; }
    if (isRedDay && !ackRedDay) { toast.error("Conferma di voler pianificare in giorno festivo/weekend"); return; }
    setSaving(true);
    try {
      const data_inizio = `${date}T${form.ora}:00`;
      await api.put(`/clienti/${form.cliente_id}/pianificazione`, {
        pannelli: form.pannelli,
        batteria: form.batteria,
        inverter: form.inverter,
        squadra_id: squadra.id,
        data_inizio,
        durata_ore: Number(form.durata_ore) || 4,
        note: form.note,
        vitto: 0,
        alloggio: 0,
      });
      toast.success("Pianificazione creata");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="card-shell w-full max-w-xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="create-pianificazione-modal">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h2 className="text-xl font-bold capitalize" style={{ fontFamily: "Manrope" }}>Nuovo appuntamento</h2>
            <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Squadra <b style={{ color: squadra.color }}>{squadra.name}</b> · {labelDate}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={save} className="p-6 space-y-4">
          {isRedDay && (
            <div className="rounded-lg p-3 flex items-start gap-2 text-sm" style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <b>{holidayName ? `Giorno festivo: ${holidayName}` : "Sabato/Domenica"}</b>
                <div className="text-xs mt-1">Stai schedulando un appuntamento in un giorno non lavorativo. Conferma se vuoi procedere comunque.</div>
                <label className="flex items-center gap-2 mt-2 text-xs font-semibold">
                  <input type="checkbox" checked={ackRedDay} onChange={(e) => setAckRedDay(e.target.checked)} data-testid="ack-red-day" />
                  Confermo di voler pianificare in questo giorno
                </label>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Cliente *</label>
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtra per nome, ID, città…"
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none mb-2"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="cliente-search"
            />
            <select
              required value={form.cliente_id}
              onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
              className="w-full px-3 py-2 text-sm rounded-md border outline-none bg-white"
              style={{ borderColor: "var(--border)" }}
              data-testid="cliente-select"
              size={6}
            >
              {loading && <option>Caricamento…</option>}
              {!loading && filtered.length === 0 && <option disabled>Nessun cliente trovato</option>}
              {!loading && filtered.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.cliente_id ? `[${c.cliente_id}] ` : ""}{c.nome} {c.cognome_ragione_sociale} {c.citta ? `· ${c.citta}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Ora inizio</label>
              <input type="time" value={form.ora} onChange={(e) => setForm({ ...form, ora: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="form-ora" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Durata (ore)</label>
              <input type="number" min={0.5} step={0.5} value={form.durata_ore}
                onChange={(e) => setForm({ ...form, durata_ore: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="form-durata" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Pannelli</label>
              <input value={form.pannelli} onChange={(e) => setForm({ ...form, pannelli: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Batteria</label>
              <input value={form.batteria} onChange={(e) => setForm({ ...form, batteria: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Inverter</label>
              <input value={form.inverter} onChange={(e) => setForm({ ...form, inverter: e.target.value })}
                className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
            <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="mt-1 w-full px-3 py-2 text-sm rounded-md border outline-none" style={{ borderColor: "var(--border)", background: "#fff" }} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Annulla</button>
            <button type="submit" className="btn-primary" disabled={saving || !form.cliente_id || (isRedDay && !ackRedDay)} data-testid="form-pianificazione-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crea appuntamento
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ---------- Modal: selezione fra più interventi nello stesso giorno ----------
function PickInterventoModal({ interventi, squadraName, date, onClose, onPick }) {
  const labelDate = new Date(date + "T00:00:00").toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "long",
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="card-shell w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pick-intervento-modal">
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>{interventi.length} appuntamenti</h3>
            <p className="text-xs capitalize" style={{ color: "var(--text-muted)" }}>{squadraName} · {labelDate}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 max-h-80 overflow-y-auto space-y-1">
          {interventi.map((iv, idx) => (
            <button
              key={`${iv.cliente_id}-${idx}`}
              onClick={() => onPick(iv.cliente_id)}
              className="w-full text-left p-3 rounded-md border hover:bg-gray-50 transition"
              style={{ borderColor: "var(--border)" }}
              data-testid={`pick-intervento-${iv.cliente_id}`}
            >
              <div className="font-semibold text-sm">{iv.cliente_nome || "Cliente"}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                {iv.ora || "—"} · {iv.durata_ore || 0}h{iv.cliente_ref ? ` · ${iv.cliente_ref}` : ""}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}




// ---------- Workload overview: barre di carico settimanale per squadra ----------
function WorkloadOverview({ squadre, calendar }) {
  if (!calendar || !squadre || squadre.length === 0) return null;
  const summary = (calendar.squadre || [])
    .filter((sq) => sq.squadra_id) // escludi "non assegnata" vuota
    .map((sq) => ({
      id: sq.squadra_id,
      name: sq.squadra_name,
      color: sq.squadra_color || "#888",
      pct: Math.min(120, sq.carico_pct || 0),
      hours: sq.ore_pianificate || 0,
      days: sq.giorni_occupati || 0,
      total_days: sq.giorni_totali_lavorativi || 5,
      capacity: sq.capacita_ore_settimana || 40,
      interventi: sq.totale_settimana || 0,
    }))
    .sort((a, b) => b.pct - a.pct);

  if (summary.length === 0) return null;

  const tone = (p) => {
    if (p >= 90) return { bg: "#FEE2E2", bar: "#DC2626", txt: "#991B1B", label: "Sovraccarica" };
    if (p >= 60) return { bg: "#FEF3C7", bar: "#D97706", txt: "#92400E", label: "Quasi piena" };
    if (p > 0)   return { bg: "#DCFCE7", bar: "#16A34A", txt: "#166534", label: "OK" };
    return { bg: "#F3F4F6", bar: "#9CA3AF", txt: "#374151", label: "Libera" };
  };

  // Aggregate KPIs
  const totalHours = summary.reduce((s, x) => s + x.hours, 0);
  const totalCapacity = summary.reduce((s, x) => s + x.capacity, 0);
  const avgLoad = totalCapacity > 0 ? Math.round((totalHours / totalCapacity) * 100) : 0;
  const overloaded = summary.filter((s) => s.pct >= 90).length;

  return (
    <div className="card-shell p-4 mb-4" data-testid="workload-overview">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md" style={{ background: "rgba(27,94,32,0.12)" }}>
            <Users2 className="w-4 h-4" style={{ color: "var(--brand-green)" }} />
          </div>
          <div>
            <h3 className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Carico squadre della settimana</h3>
            <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>Capacità giornaliera = 8h × 5 giorni lavorativi</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span style={{ color: "var(--text-muted)" }}>Media: <b style={{ color: "var(--text-primary)" }}>{avgLoad}%</b></span>
          <span style={{ color: "var(--text-muted)" }}>Totale ore: <b style={{ color: "var(--text-primary)" }}>{totalHours.toFixed(1)}h</b></span>
          {overloaded > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="workload-overloaded-count">
              <AlertTriangle className="w-3 h-3" /> {overloaded} sovraccarica
            </span>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {summary.map((sq) => {
          const t = tone(sq.pct);
          const barWidth = Math.min(100, sq.pct);
          return (
            <div
              key={sq.id}
              className="rounded-lg p-3"
              style={{ background: t.bg, border: `1px solid ${t.bar}33` }}
              data-testid={`workload-card-${sq.id}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: sq.color }} />
                  <span className="font-semibold text-sm truncate" title={sq.name}>{sq.name}</span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: t.txt }}>{t.label}</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.6)" }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${barWidth}%`, background: t.bar }}
                  data-testid={`workload-bar-${sq.id}`}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5 text-[11px]" style={{ color: t.txt }}>
                <span className="font-mono"><b>{sq.pct}%</b> · {sq.hours}h / {sq.capacity}h</span>
                <span>{sq.days}/{sq.total_days} gg · {sq.interventi} app.</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
